import type { OperatorApi } from '../../api/client'
import { ApiError } from '../../api/client'
import { describeWriteFailure, type WriteFailure } from '../../api/writeFailure'
import { verifyExpiry, isReadBackProblem } from '../../api/readBack'
import type { TokenIssuanceResponse } from '../../api/types'
import type { PlanStep, WizardInput } from './wizardMachine'

// The wizard executor (ATC-133).
//
// FIVE NON-TRANSACTIONAL WRITES ACROSS THREE RESOURCES, with no transaction
// available and none coming. Everything below follows from that.
//
// THERE IS NO AUTOMATIC COMPENSATION, DELIBERATELY. Three reasons, and the
// third is decisive:
//
//   1. There is no DELETE in the API. The only "undo" for an issued
//      environment is `status: 'revoked'`, which WRITES A REVOCATION THAT NEVER
//      HAD A BUSINESS MEANING — a permanent, audited record of a customer
//      being terminated who was never onboarded.
//   2. A grant cannot be undone at all; the ledger is append-only. The nearest
//      thing is an audited negative adjustment, which is a deliberate operator
//      decision with a mandatory reason, not something a rollback should mint.
//   3. A `fetch` rejection is NOT PROOF THE SERVER DID NOTHING. Compensating an
//      "unknown" failure could delete something that succeeded.

export type StepOutcome = 'ok' | 'failed' | 'skipped'

export interface StepResult {
  step: PlanStep
  outcome: StepOutcome
  /** Identifiers only — never token material. */
  detail?: string
  failure?: WriteFailure
  /**
   * ATC-139. Set when the server accepted the write but did not apply the
   * expiry that was asked for. The step still succeeded — this is not a
   * failure, it is a SUCCESS THAT DID LESS THAN IT SAID, which is the shape
   * Hotfix 054 had and the shape nothing was watching for.
   */
  readBackWarning?: string
}

export interface RunResult {
  results: StepResult[]
  accountId: string | null
  customerEnvironmentId: string | null
  /**
   * ATC-141. The server-minted environment label, so the result screen can
   * name what it links to instead of printing a GUID at an operator who has
   * just finished a five-step provisioning flow.
   */
  environmentLabel: string | null
  /** True when every step succeeded. */
  complete: boolean
}

export interface RunPlanDeps {
  api: OperatorApi
  input: WizardInput
  /**
   * THE REVEAL GATE, and it is the reason this is a parameter rather than a
   * callback fired and forgotten.
   *
   * A raw token is shown exactly once and is never re-fetchable. Batching the
   * reveals to the end would hold an unrecoverable secret in JS memory across
   * three more network calls, any of which can fail, navigate, or throw. So the
   * executor AWAITS this: no write happens while an unacknowledged token is on
   * screen. Injecting it makes that a testable invariant rather than a
   * convention someone can quietly break.
   */
  reveal: (
    result: TokenIssuanceResponse,
    principalClass: 'device' | 'flightdeck',
    readBackWarning?: string,
  ) => Promise<void>
  /** Called after each step so the UI can render progress. */
  onProgress?: (results: StepResult[]) => void
}

export async function runPlan(steps: PlanStep[], deps: RunPlanDeps): Promise<RunResult> {
  const { api, input, reveal, onProgress } = deps
  const results: StepResult[] = []
  let accountId: string | null = null
  let customerEnvironmentId: string | null = null
  let environmentLabel: string | null = null

  function record(result: StepResult) {
    results.push(result)
    onProgress?.([...results])
  }

  function markRemainingSkipped(from: number) {
    for (let i = from; i < steps.length; i++) {
      record({ step: steps[i], outcome: 'skipped' })
    }
  }

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    try {
      switch (step.kind) {
        case 'account': {
          const account = await api.createAccount(input.customerName.trim())
          accountId = account.accountId
          record({ step, outcome: 'ok', detail: account.accountId })
          break
        }

        case 'grant': {
          if (!accountId) {
            throw new Error('No account id — the account step did not complete.')
          }
          const grant = await api.grantMigrationLicense(accountId, step.licenseType!, step.amount!)
          record({ step, outcome: 'ok', detail: grant.grantId })
          break
        }

        case 'environment': {
          const issued = await api.issueEnvironment({
            // ATC-140: no name. The server mints the label.
            entitledServices: step.entitledServices,
            expiresAt: step.expiresAt,
          })
          customerEnvironmentId = issued.customerEnvironmentId
          environmentLabel = issued.customerName || null
          // ATC-139. Verified BEFORE the reveal, so the discrepancy is on the
          // one screen the operator is certain to read, beside the token it
          // describes.
          const envReadBack = verifyExpiry(step.expiresAt, issued.expiresAt)
          const envWarning = isReadBackProblem(envReadBack) ? envReadBack.message : undefined
          // REVEALED THE MOMENT IT IS MINTED, and the executor blocks here.
          await reveal(issued, 'device', envWarning)
          record({
            step,
            outcome: 'ok',
            detail: issued.customerEnvironmentId,
            readBackWarning: envWarning,
          })
          break
        }

        case 'map': {
          if (!accountId || !customerEnvironmentId) {
            throw new Error('Missing account or environment id from an earlier step.')
          }
          await api.mapAccountEnvironment({
            accountId,
            customerEnvironmentId,
            // ATC-140 (loose end from ATC-138). `accountName` is the
            // PRE-registry copy of the name and the registry now owns it. It
            // was harmless here — the wizard had just created the account with
            // this exact string — but it was the last writer of a field the
            // environment page already stopped sending.
          })
          record({ step, outcome: 'ok', detail: `${customerEnvironmentId} → ${accountId}` })
          break
        }

        case 'flightdeck': {
          if (!customerEnvironmentId) {
            throw new Error('Missing environment id from an earlier step.')
          }
          const fd = await api.issueFlightdeckCredential({
            customerEnvironmentId,
            entitledServices: step.entitledServices,
            // ATC-139. The SAME term as the device credential. Omitting it
            // minted a perpetual `atc_fd_` credential beside a time-boxed
            // device one — exactly the state `ATC-ADR-011`'s all-rows PATCH
            // default exists to prevent.
            expiresAt: step.expiresAt,
          })
          const fdReadBack = verifyExpiry(step.expiresAt, fd.expiresAt)
          const fdWarning = isReadBackProblem(fdReadBack) ? fdReadBack.message : undefined
          await reveal(fd, 'flightdeck', fdWarning)
          record({
            step,
            outcome: 'ok',
            detail: customerEnvironmentId,
            readBackWarning: fdWarning,
          })
          break
        }
      }
    } catch (err) {
      const failure = describeWriteFailure(err)
      record({ step, outcome: 'failed', failure })
      // STOP. Every later step depends on an id this one was supposed to
      // produce, or compounds the mess. Continuing would turn one recoverable
      // partial state into several.
      markRemainingSkipped(i + 1)
      return { results, accountId, customerEnvironmentId, environmentLabel, complete: false }
    }
  }

  return { results, accountId, customerEnvironmentId, environmentLabel, complete: true }
}

export type RecoveryKind = 'retry' | 'go-and-look' | 'rotate' | 'lever'

export interface Recovery {
  kind: RecoveryKind
  message: string
  /** Where the operator goes to finish by hand. */
  href?: string
  linkLabel?: string
}

/**
 * What an operator should do about a failed step.
 *
 * THE ONE RULE THIS ENCODES: a retry is offered only when it is BOTH safe and
 * useful. `not-applied` means the server refused before writing anything, so a
 * retry cannot duplicate. `unknown` means it may or may not have landed — and
 * a one-click retry there mints a duplicate environment plus a token nobody
 * holds, or double-grants an append-only ledger.
 */
export function recoveryFor(result: StepResult, accountId: string | null): Recovery {
  const failure = result.failure
  const kind = result.step.kind

  // A 409 on the flightdeck mint is INFORMATIVE, not an error: it proves the
  // credential already exists, which means an earlier attempt succeeded and
  // its token — revealed once — is lost. Rotation is the remedy, and it is the
  // only path that produces a token the operator can hold.
  if (
    kind === 'flightdeck' &&
    failure?.code === 'flightdeck_principal_already_issued'
  ) {
    return {
      kind: 'rotate',
      message:
        'The Flight Deck credential already exists, which means an earlier attempt succeeded and its token was never captured. Rotate it from the environment page to get a token you can hold.',
    }
  }

  // GRANTS ARE NEVER AUTO-RETRIED, whatever the classification. The ledger is
  // append-only: a retry of a grant that actually landed double-grants, and
  // the correction is an audited adjustment with a mandatory reason.
  if (kind === 'grant') {
    return {
      kind: 'lever',
      message:
        failure?.outcome === 'unknown'
          ? 'This grant may or may not have been recorded. Check the balance before granting again — the ledger is append-only, so a repeat would double-grant.'
          : 'The grant was refused and nothing was recorded. Grant it from the customer page.',
      href: accountId ? `/customers/${accountId}` : '/customers',
      linkLabel: 'Open the customer',
    }
  }

  if (failure?.outcome === 'unknown') {
    // NO RETRY BUTTON. The copy says so plainly and points at the list.
    if (kind === 'environment') {
      return {
        kind: 'go-and-look',
        message:
          'The environment may or may not have been created — the server did not say. Do not retry: a second attempt would mint a duplicate environment and a token nobody holds. Check the list first.',
        href: '/environments',
        linkLabel: 'Open the environments list',
      }
    }
    return {
      kind: 'go-and-look',
      message: 'This step may or may not have taken effect. Check before trying again.',
    }
  }

  return {
    kind: 'retry',
    message: 'Nothing was written. Fix the input and try this step again from its own page.',
  }
}

/** Narrow helper so the page can special-case a 409 without importing ApiError. */
export function isAlreadyIssued(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'flightdeck_principal_already_issued'
}
